import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { PinnedState, StateChainItem, StateService } from '@app/core/services/state.service';
import { UserStore } from '@app/core/services/user.store';
import { NavigationHistoryService } from '@app/core/services/navigation-history.service';
import { PinnedStateService } from '@app/core/services/pinned-state.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'tt-app-breadcrumbs',
    templateUrl: './app-breadcrumbs.component.html',
    styleUrls: ['./app-breadcrumbs.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class AppBreadcrumbsComponent implements OnInit, OnDestroy {
    public pinnedStates: PinnedState[] = [];

    public breadCrumbs: StateChainItem[] = [];

    public selectedState = '';

    private readonly destroy$ = new Subject<void>();

    public get breadcrumbView(): 'true' | 'false' | 'simple' {
        return this._breadcrumbView;
    }
    public set breadcrumbView(value: string) {
        if (['true', 'false', 'simple'].includes(value)) {
            this._breadcrumbView = value as 'true' | 'false' | 'simple';
        } else {
            this._breadcrumbView = 'true';
        }
    }
    private _breadcrumbView: 'true' | 'false' | 'simple' = 'true';

    constructor(
        public state: StateService,
        public user: UserStore,
        private readonly history: NavigationHistoryService,
        private readonly pins: PinnedStateService,
    ) {
        // console.log(user.breadcrumbView);
        // user.breadcrumbViewChanged.subscribe({ next: (value) => console.log(value) });

        this.breadcrumbView = user.breadcrumbView;
        user.breadcrumbViewChanged.pipe(takeUntil(this.destroy$)).subscribe({ next: (value) => (this.breadcrumbView = value) });
    }

    public onStateChainItemClick(_: MouseEvent, stateChainItem: StateChainItem) {
        this.state.goBackTo(stateChainItem);
    }

    public onPinnedStateClick(_: MouseEvent, pinnedState: PinnedState) {
        this.state.pinnedGo(pinnedState.pinned_url);
    }

    public async pin(event: { stateChainItem: StateChainItem; index: number }) {
        const stateToPin = event.stateChainItem;
        const pin = await this.pins.pin(stateToPin);
        if (pin && this.state.activeState$.value?.path === pin.pinned_url) this.selectedState = pin.webpagepinned_keyno;
    }

    public removeStateChainItem(event: { stateChainItem: StateChainItem; index: number }) {
        this.history.remove(event.stateChainItem.id);
    }

    public async unPin(event: { pin: PinnedState; index: number }) {
        const unpinnedState = event.pin;
        const wasSelected = unpinnedState.webpagepinned_keyno === this.selectedState;
        const removed = await this.pins.unpin(unpinnedState);

        if (removed && wasSelected) this.selectedState = this.state.activeState$.value?.id ?? '';
    }

    public async loadPinnedStates() {
        await this.pins.load(true);
    }

    private async init() {
        await this.loadPinnedStates();

        const currentPin = this.pins.findByUrl(this.state.getCurrentPath());
        if (currentPin) this.selectedState = currentPin.webpagepinned_keyno;

        this.pins.pins$.pipe(takeUntil(this.destroy$)).subscribe({
            next: (pins) => {
                this.pinnedStates = pins;
                this.updateBreadCrumbs(this.state.stateChain$.value);
            },
        });

        this.state.stateChain$.pipe(takeUntil(this.destroy$)).subscribe({
            next: (chain) => {
                this.updateBreadCrumbs(chain);
            },
        });

        this.state.activeState$.pipe(takeUntil(this.destroy$)).subscribe({
            next: (state) => {
                if (state !== null) {
                    const pinnedStateWithPath = this.pins.findByUrl(state.path);

                    if (!!pinnedStateWithPath) {
                        this.selectedState = pinnedStateWithPath.webpagepinned_keyno;
                        void this.pins.syncTitle(state);
                    } else {
                        this.selectedState = state.id;
                    }
                }
            },
        });
    }

    private updateBreadCrumbs(chain: StateChainItem[]): void {
        const pinnedPaths = new Set(this.pinnedStates.map((pin) => pin.pinned_url));
        this.breadCrumbs = chain.filter((state) => !pinnedPaths.has(state.path)).reverse();
    }

    ngOnInit(): void {
        void this.init();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
